export const NATIVE_APPLICATION_ID = "com.portability.app" as const;
export const NATIVE_RELEASE_VERSION = {
  marketingVersion: "1.0.0",
  appleBuildNumber: 27,
  androidVersionCode: 18
} as const;

export const NATIVE_SDK_REQUIREMENTS = {
  apple: {
    minimumBuildSdkMajor: 26,
    minimumXcodeMajor: 26,
    minimumDeploymentTarget: "16.0"
  },
  android: {
    compileSdk: 36,
    targetSdk: 36,
    minimumSdk: 24,
    playBillingMajor: 9
  }
} as const;

export const MOBILE_STORE_PRODUCTS = {
  insiderMonthly: {
    id: "com.portability.app.insider.monthly",
    kind: "AUTO_RENEWABLE_SUBSCRIPTION",
    tier: "INSIDER",
    priceMinorUsd: 2_500,
    period: "P1M"
  },
  creatorMonthly: {
    id: "com.portability.app.creator.monthly",
    kind: "AUTO_RENEWABLE_SUBSCRIPTION",
    tier: "CREATOR",
    priceMinorUsd: 5_000,
    period: "P1M"
  },
  kidsDownload: {
    id: "com.portability.app.kids.download",
    kind: "CONSUMABLE",
    entitlement: "ONE_KIDS_DOWNLOAD"
  },
  creatorTipSmall: {
    id: "com.portability.app.creator.tip.small",
    kind: "CONSUMABLE",
    amountMinorUsd: 100
  },
  creatorTipMedium: {
    id: "com.portability.app.creator.tip.medium",
    kind: "CONSUMABLE",
    amountMinorUsd: 500
  },
  creatorTipLarge: {
    id: "com.portability.app.creator.tip.large",
    kind: "CONSUMABLE",
    amountMinorUsd: 1_000
  }
} as const;

export type MobileStoreProductId = (typeof MOBILE_STORE_PRODUCTS)[keyof typeof MOBILE_STORE_PRODUCTS]["id"];
const ALLOWED_PRODUCT_IDS = new Set<MobileStoreProductId>(Object.values(MOBILE_STORE_PRODUCTS).map(({ id }) => id));

export function assertKnownMobileStoreProduct(productId: string): asserts productId is MobileStoreProductId {
  if (!ALLOWED_PRODUCT_IDS.has(productId as MobileStoreProductId)) {
    throw new Error(`Unknown mobile store product: ${productId}`);
  }
}

export type StorePurchaseEvidence = Readonly<{
  platform: "APPLE" | "GOOGLE";
  productId: MobileStoreProductId;
  signedTransactionJws?: string;
  purchaseToken?: string;
  packageName: typeof NATIVE_APPLICATION_ID;
  environment: "SANDBOX" | "PRODUCTION";
}>;

export function createServerVerificationSubmission(input: StorePurchaseEvidence): StorePurchaseEvidence & Readonly<{
  locallyGrantedEntitlement: false;
  serverVerificationRequired: true;
  serverReconciliationRequired: true;
}> {
  assertKnownMobileStoreProduct(input.productId);
  const platformEvidence = input.platform === "APPLE" ? input.signedTransactionJws : input.purchaseToken;
  if (!platformEvidence?.trim()) throw new Error("Signed store evidence is required before server verification");
  if (input.packageName !== NATIVE_APPLICATION_ID) throw new Error("Store evidence package identifier mismatch");
  return {
    ...input,
    locallyGrantedEntitlement: false,
    serverVerificationRequired: true,
    serverReconciliationRequired: true
  };
}

export const STORE_LIFECYCLE_AUTHORITY = {
  deviceReceiptIsAuthority: false,
  serverEntitlementIsAuthority: true,
  appleApi: "App Store Server API",
  appleNotifications: "App Store Server Notifications V2",
  googleApi: "Google Play Developer API",
  googleNotifications: "Real-time developer notifications",
  restoreRequiresExplicitUserAction: true,
  appleTransactionUpdatesRequired: true,
  annualProductsAllowed: false,
  trialsAllowed: false,
  discountedMonthlyPromotionsAllowed: false
} as const;

export const CONTEXTUAL_PERMISSION_POLICY = {
  location: {
    allowedContexts: ["nearby_search", "map_current_location", "local_recommendations"],
    fallback: "manual_location_entry",
    childAllowed: false
  },
  microphone: {
    allowedContexts: ["push_to_talk", "kids_voice_creation_with_verified_parent"],
    fallback: "typed_input",
    childAllowed: true
  },
  camera: {
    allowedContexts: ["profile_photo", "creator_broadcast", "kids_picture_input_with_verified_parent"],
    fallback: "file_picker_or_skip",
    childAllowed: true
  },
  notifications: {
    allowedContexts: ["event_reminder_opt_in", "trip_update_opt_in", "creator_follow_opt_in"],
    fallback: "in_app_updates",
    childAllowed: false
  }
} as const;

export type NativePermission = keyof typeof CONTEXTUAL_PERMISSION_POLICY;
export type PermissionContext = (typeof CONTEXTUAL_PERMISSION_POLICY)[NativePermission]["allowedContexts"][number];

export function authorizeContextualPermissionRequest(input: {
  permission: NativePermission;
  context: string;
  initiatedByUser: boolean;
  childSession: boolean;
  verifiedParentPresent?: boolean;
}): Readonly<{ permission: NativePermission; fallback: string; mayRequestSystemPrompt: true }> {
  const policy = CONTEXTUAL_PERMISSION_POLICY[input.permission];
  if (!input.initiatedByUser) throw new Error("Permissions may only be requested after a user action");
  if (!policy.allowedContexts.includes(input.context as never)) throw new Error("Permission context is not allowlisted");
  if (input.childSession && !policy.childAllowed) throw new Error("Permission is unavailable in a child session");
  if (input.childSession && input.permission !== "microphone" && input.permission !== "camera") {
    throw new Error("Child permission boundary rejected the request");
  }
  if (input.childSession && input.verifiedParentPresent !== true) {
    throw new Error("A verified parent must be present for child media permissions");
  }
  return { permission: input.permission, fallback: policy.fallback, mayRequestSystemPrompt: true };
}

export const DEEP_LINK_HOSTS = ["portabilityapp.net", "www.portabilityapp.net"] as const;
export const DEEP_LINK_KINDS = ["trip", "place", "offer", "event", "creator", "stream", "member"] as const;
export type DeepLinkKind = (typeof DEEP_LINK_KINDS)[number];

export function resolveDeepLink(rawUrl: string): Readonly<{ kind: DeepLinkKind; publicId: string; canonicalUrl: string }> {
  const url = new URL(rawUrl);
  if (url.protocol !== "https:" || !DEEP_LINK_HOSTS.includes(url.hostname as (typeof DEEP_LINK_HOSTS)[number])) {
    throw new Error("Deep link origin is not allowlisted");
  }
  const segments = url.pathname.split("/").filter(Boolean);
  const kind = segments[0];
  const publicId = segments[1];
  if (segments.length !== 2 || !kind || !DEEP_LINK_KINDS.includes(kind as DeepLinkKind) || !publicId || !/^[A-Za-z0-9_-]{1,128}$/.test(publicId)) {
    throw new Error("Deep link route is invalid");
  }
  return { kind: kind as DeepLinkKind, publicId, canonicalUrl: `https://portabilityapp.net/${kind}/${publicId}` };
}

export function createPushRegistrationPlan(input: {
  initiatedByUser: boolean;
  childSession: boolean;
  notificationContext: string;
}): Readonly<{
  registerEachLaunch: true;
  cacheTokenAsAuthority: false;
  sendTokenToServerOverTls: true;
  fallback: "in_app_updates";
}> {
  authorizeContextualPermissionRequest({
    permission: "notifications",
    context: input.notificationContext,
    initiatedByUser: input.initiatedByUser,
    childSession: input.childSession
  });
  return {
    registerEachLaunch: true,
    cacheTokenAsAuthority: false,
    sendTokenToServerOverTls: true,
    fallback: "in_app_updates"
  };
}

export const NATIVE_ACCESSIBILITY_CONTRACT = {
  dynamicType: true,
  talkBackAndVoiceOverLabels: true,
  minimumTapTargetPoints: 44,
  reducedMotionRespected: true,
  highContrastSupported: true,
  keyboardNavigationSupported: true
} as const;
